import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
} from '@angular/core';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { faImage } from '@fortawesome/free-regular-svg-icons';
import {
  faExpandArrowsAlt,
  faSearchMinus,
  faSearchPlus,
  faTh,
} from '@fortawesome/free-solid-svg-icons';
import { noop, times } from '@sama/micro-dash';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { LoadImageService } from '../../../shared/misc-services/load-image.service';
import { isLeftButton } from '../../../shared/misc-utilities/event-utils';

@Component({
  selector: 'h3-image',
  templateUrl: 'image.component.html',
  styleUrls: ['image.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ImageComponent implements OnChanges, OnInit {
  @Input() gridSize?: string;
  @Input() gridColor?: string;
  @Input({ required: true }) url!: string;
  showGrid!: boolean;
  status$ = new BehaviorSubject('loading');
  transform$: Observable<SafeStyle>;
  zoom$ = new BehaviorSubject(1);
  gridRows?: unknown[];
  gridColumns?: unknown[];
  gridTemplateColumns?: SafeStyle;

  faSearchPlus = faSearchPlus;
  faSearchMinus = faSearchMinus;
  faExpandArrowsAlt = faExpandArrowsAlt;
  faImage = faImage;
  faTh = faTh;

  private $element: JQuery;
  private offset$ = new BehaviorSubject({ x: 0, y: 0 });
  private image?: HTMLImageElement;

  constructor(
    elementRef: ElementRef,
    private domSanitizer: DomSanitizer,
    private loadImageService: LoadImageService,
  ) {
    this.$element = $(elementRef.nativeElement);
    this.transform$ = combineLatest([this.zoom$, this.offset$]).pipe(
      map(([zoom, offset]) =>
        domSanitizer.bypassSecurityTrustStyle(
          `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
        ),
      ),
    );
  }

  ngOnInit() {
    this.showGrid = !!this.gridSize;
  }

  async ngOnChanges() {
    let numRows: number;
    let numCols: number;

    await this.loadImage();

    if (!this.gridSize) {
      numRows = numCols = 0;
    } else if (this.gridSize.includes(',')) {
      [numRows, numCols] = this.gridSize.split(',').map((n) => +n);
    } else {
      numRows = numCols = +this.gridSize;
    }
    this.gridRows = times(numRows, noop);
    this.gridColumns = times(numCols, noop);
    this.gridTemplateColumns = this.domSanitizer.bypassSecurityTrustStyle(
      `repeat(${numCols}, auto)`,
    );
  }

  changeZoom(factor: number) {
    this.zoom$.next(this.zoom$.value * factor);
  }

  startPanning(event: MouseEvent) {
    if (!isLeftButton(event)) {
      return;
    }

    let lastDragEvent = event;
    const panHandler = (e: MouseEvent) => {
      e.preventDefault();
      const { x: lastX, y: lastY } = this.offset$.value;
      this.offset$.next({
        x: lastX + e.clientX - lastDragEvent.clientX,
        y: lastY + e.clientY - lastDragEvent.clientY,
      });
      lastDragEvent = e;
    };
    document.addEventListener('mousemove', panHandler);
    document.addEventListener('mouseup', () =>
      document.removeEventListener('mousemove', panHandler),
    );
  }

  reset() {
    if (!this.image) {
      return;
    }
    const imgWidth = this.$element.find('.component-container').width()!;
    const displayedWidth = Math.min(imgWidth, this.image.width);

    this.zoom$.next(Math.min(1, this.image.width / displayedWidth));
    this.offset$.next({ x: 0, y: 0 });
  }

  private async loadImage() {
    if (this.url) {
      try {
        this.status$.next('loading');
        this.image = await this.loadImageService.loadImage(this.url, null);
        this.reset();
        this.status$.next('loaded');
      } catch {
        this.status$.next('error');
      }
    } else {
      this.status$.next('no url');
    }
  }
}
